/*
  Oppie welkomstkorting popup, gedrag.

  - Opent na X seconden, of eerder bij Y% scrollen (nooit binnen 5 s), of bij
    exit-intent op desktop. Op mobiel optioneel pas vanaf de tweede pagina
    (Google's interstitial-regel).
  - Een keer tonen, daarna niet meer: zodra de popup OPENT (welke trigger ook)
    zetten we een tijdstempel in localStorage (cookie als fallback) en ruimen we
    alle andere triggers op. Pas na repeat_days dagen mag hij weer automatisch.
  - Kruisje wordt pas na close_delay seconden zichtbaar; Escape ook pas dan.
  - Elke antwoordklik: opslaan, loggen naar het Sheet-webhook, door naar de
    volgende stap. E-mail: POST naar /contact (customer form, tags met de
    antwoorden), daarna direct de code tonen. Aanbeveling matcht op antwoord.
  - Wie de code claimt ziet 'm niet meer automatisch (localStorage), het
    vouwtje blijft zodat de code bereikbaar blijft.

  State keys:
    localStorage   oppie:discount:claimed     -> permanent uit na claimen
    localStorage   oppie:discount:shown-at    -> tijdstip (ms) waarop hij opende
    cookie         oppie_discount_shown       -> zelfde, als localStorage niet werkt
    sessionStorage oppie:discount:tab-hidden  -> vouwtje deze sessie weggeklikt
    sessionStorage oppie:discount:pages       -> aantal pagina's deze sessie
    sessionStorage oppie:discount:sid         -> sessie-id voor het logboek
*/

const CLAIMED = 'oppie:discount:claimed';
const SHOWN_AT = 'oppie:discount:shown-at';
const SHOWN_COOKIE = 'oppie_discount_shown';
const DAY_MS = 24 * 60 * 60 * 1000;
const TAB_HIDDEN = 'oppie:discount:tab-hidden';
const PAGES = 'oppie:discount:pages';
const SID = 'oppie:discount:sid';
const MIN_DWELL_MS = 5000;

const readItem = (store, key) => {
  try {
    return store.getItem(key);
  } catch (error) {
    return null;
  }
};

const writeItem = (store, key, value) => {
  try {
    store.setItem(key, value);
  } catch (error) {
    /* private mode / storage uit: gedrag degradeert netjes */
  }
};

const readFlag = (store, key) => readItem(store, key) === '1';
const writeFlag = (store, key) => writeItem(store, key, '1');

const readCookie = (name) => {
  try {
    const hit = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`));
    return hit ? decodeURIComponent(hit.slice(name.length + 1)) : null;
  } catch (error) {
    return null;
  }
};

const writeCookie = (name, value, maxAgeSeconds) => {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
  } catch (error) {
    /* cookies uit: dan alleen het geheugen van deze pagina */
  }
};

// Tijdstip van de laatste keer dat de popup opende, uit localStorage of de cookie.
const readShownAt = () => {
  const raw = readItem(window.localStorage, SHOWN_AT) || readCookie(SHOWN_COOKIE);
  const value = Number.parseInt(raw || '', 10);
  return Number.isFinite(value) ? value : 0;
};

const writeShownAt = (repeatMs) => {
  const now = String(Date.now());
  writeItem(window.localStorage, SHOWN_AT, now);
  // Cookie altijd mee: werkt ook als localStorage geblokkeerd is of gewist wordt.
  writeCookie(SHOWN_COOKIE, now, Math.round(repeatMs / 1000));
};

const getSessionId = () => {
  const existing = readItem(window.sessionStorage, SID);
  if (existing) return existing;
  const fresh = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  writeItem(window.sessionStorage, SID, fresh);
  return fresh;
};

const bumpPageCount = () => {
  const count = Number.parseInt(readItem(window.sessionStorage, PAGES) || '0', 10) + 1;
  writeItem(window.sessionStorage, PAGES, String(count));
  return count;
};

const isMobile = () => window.matchMedia('(max-width: 899px)').matches;

const initDiscountPopup = () => {
  const root = document.querySelector('[data-oppie-discount]');
  if (!root) return;

  const screen = root.querySelector('[data-od-screen]');
  const panel = root.querySelector('[data-od-panel]');
  const closeBtn = root.querySelector('[data-od-close]');
  const steps = Array.from(root.querySelectorAll('[data-od-step]'));
  const form = root.querySelector('[data-od-form]');
  const tagsInput = root.querySelector('[data-od-tags]');
  const submitBtn = root.querySelector('[data-od-submit]');
  const copyBtn = root.querySelector('[data-od-copy]');
  const shopBtn = root.querySelector('[data-od-shop]');
  const sentLine = root.querySelector('[data-od-sent]');
  const recos = Array.from(root.querySelectorAll('[data-od-reco]'));
  const tab = root.querySelector('[data-od-tab]');
  const tabOpenBtn = root.querySelector('[data-od-tab-open]');
  const tabCloseBtn = root.querySelector('[data-od-tab-close]');

  const code = root.dataset.code || '';
  const delayMs = (Number.parseInt(root.dataset.delay, 10) || 12) * 1000;
  const scrollPercent = Number.parseInt(root.dataset.scroll, 10) || 0;
  const exitIntent = root.dataset.exitIntent === 'true';
  const mobileSecondPage = root.dataset.mobileSecondPage === 'true';
  const closeDelayMs = (Number.parseInt(root.dataset.closeDelay, 10) || 0) * 1000;
  const logEndpoint = root.dataset.logEndpoint || '';
  const shopUrl = root.dataset.shopUrl || '/collections/all';
  const locale = root.dataset.locale || '';
  const repeatMs = (Number.parseInt(root.dataset.repeatDays, 10) || 30) * DAY_MS;

  const order = steps.map((step) => step.dataset.odStep);
  const answers = {};
  const sessionId = getSessionId();
  const pageCount = bumpPageCount();
  const startedAt = Date.now();
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  let opened = false; // staat nu open
  let disarmTriggers = () => {}; // ruimt timer, scroll- en exit-listeners op
  let closeReady = closeDelayMs === 0;
  let lastFocus = null;

  root.hidden = false;

  /* --- logboek (Google Sheet via Apps Script) ------------------------------ */
  const log = (event, extra = {}) => {
    if (!logEndpoint || designMode) return;
    const row = {
      t: new Date().toISOString(),
      sid: sessionId,
      event,
      page: window.location.pathname,
      device: isMobile() ? 'mobiel' : 'desktop',
      locale,
      ...extra,
    };
    try {
      window.fetch(logEndpoint, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(row),
      });
    } catch (error) {
      /* loggen is best-effort */
    }
  };

  /* --- stappen ---------------------------------------------------------------- */
  const currentStep = () => steps.find((step) => step.classList.contains('is-active'));

  const focusStep = (step) => {
    const target = step.querySelector('input[type="email"], [data-od-copy]');
    if (target) window.setTimeout(() => target.focus({ preventScroll: true }), 60);
  };

  const showStep = (name) => {
    steps.forEach((step) => {
      const active = step.dataset.odStep === name;
      step.classList.toggle('is-active', active);
      step.hidden = !active;
    });
    if (panel) panel.scrollTo({ top: 0 });
    const step = currentStep();
    if (step) focusStep(step);
  };

  const nextStep = () => {
    const step = currentStep();
    const index = step ? order.indexOf(step.dataset.odStep) : -1;
    showStep(order[Math.min(index + 1, order.length - 1)]);
  };

  const prevStep = () => {
    const step = currentStep();
    const index = step ? order.indexOf(step.dataset.odStep) : 0;
    showStep(order[Math.max(index - 1, 0)]);
  };

  /* --- aanbeveling ------------------------------------------------------------ */
  const buildDiscountUrl = (target) => {
    let path = target || shopUrl;
    try {
      const url = new URL(path, window.location.origin);
      path = `${url.pathname}${url.search}`;
    } catch (error) {
      /* relatieve URL, laat staan */
    }
    return `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(path)}`;
  };

  const applyReco = () => {
    const values = Object.values(answers).map((answer) => answer.value);
    const match = recos.find((reco) => values.includes(reco.dataset.odReco));
    recos.forEach((reco) => {
      reco.hidden = reco !== match;
    });
    if (!shopBtn) return;
    if (match) {
      shopBtn.href = buildDiscountUrl(match.dataset.url);
      if (match.dataset.cta) shopBtn.textContent = match.dataset.cta;
    } else {
      shopBtn.href = buildDiscountUrl(shopUrl);
      shopBtn.textContent = shopBtn.dataset.defaultCta || shopBtn.textContent;
    }
  };

  /* --- openen / sluiten ------------------------------------------------------- */
  const lockScroll = () => document.documentElement.classList.add('oppie-discount-open');
  const unlockScroll = () => document.documentElement.classList.remove('oppie-discount-open');

  // Shopify Inbox-launcher ligt op z-index 2147483000 en dus over de popup heen.
  // Inline style, geen CSS-regel: <shopify-chat> staat niet in theme-markup en de
  // CSS-subset-pruner zou zo'n regel strippen (zelfde aanpak als oppie-cookie-consent.js).
  const CHAT_WIDGETS = 'shopify-chat, #shopify-chat, shopify-agent';
  let chatObserver = null;
  const hideChat = () => {
    document.querySelectorAll(CHAT_WIDGETS).forEach((el) => el.style.setProperty('display', 'none', 'important'));
  };
  const parkChat = () => {
    hideChat();
    if (chatObserver) return;
    chatObserver = new MutationObserver(hideChat);
    chatObserver.observe(document.body, { childList: true });
  };
  const restoreChat = () => {
    if (chatObserver) chatObserver.disconnect();
    chatObserver = null;
    document.querySelectorAll(CHAT_WIDGETS).forEach((el) => el.style.removeProperty('display'));
  };

  // De foto staat op loading="lazy" en is verborgen tot de popup opent; laad 'm alvast
  // een paar seconden vóór het openen zodat er geen grijs vlak verschijnt.
  const preloadMedia = () => {
    root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  };

  const showTab = () => {
    if (!tab) return;
    if (readFlag(window.sessionStorage, TAB_HIDDEN)) return;
    tab.hidden = false;
    window.requestAnimationFrame(() => tab.classList.add('is-open'));
  };

  const hideTab = () => {
    if (!tab) return;
    tab.classList.remove('is-open');
    tab.hidden = true;
  };

  const armClose = () => {
    if (!closeBtn) return;
    if (closeReady) {
      closeBtn.classList.add('is-ready');
      return;
    }
    window.setTimeout(() => {
      closeReady = true;
      closeBtn.classList.add('is-ready');
    }, closeDelayMs);
  };

  const openScreen = (trigger) => {
    if (!screen || opened) return;
    opened = true;
    // Gezien = op het moment van openen, niet pas bij sluiten. Daarna mag geen
    // enkele andere trigger hem nog openen, ook niet op een volgende pagina.
    if (!designMode) writeShownAt(repeatMs);
    disarmTriggers();
    lastFocus = document.activeElement;
    hideTab();
    if (!currentStep()) showStep(readFlag(window.localStorage, CLAIMED) ? 'code' : order[0]);
    preloadMedia();
    screen.hidden = false;
    lockScroll();
    parkChat();
    window.requestAnimationFrame(() => screen.classList.add('is-open'));
    armClose();
    applyReco();
    log('open', { trigger: trigger || '', pagina_nr: pageCount });
  };

  const closeScreen = (reason) => {
    if (!screen || screen.hidden) return;
    opened = false;
    screen.classList.remove('is-open');
    unlockScroll();
    restoreChat();
    const step = currentStep();
    log('close', { reason: reason || '', stap: step ? step.dataset.odStep : '' });
    const finish = () => {
      screen.hidden = true;
      screen.removeEventListener('transitionend', finish);
      showTab();
    };
    screen.addEventListener('transitionend', finish);
    window.setTimeout(finish, 420);
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  };

  /* --- antwoorden ------------------------------------------------------------- */
  const onOptionClick = (event) => {
    const button = event.target.closest('[data-od-option]');
    if (!button) return;
    const step = button.closest('[data-od-question]');
    if (!step) return;
    step.querySelectorAll('[data-od-option]').forEach((option) => option.classList.remove('is-selected'));
    button.classList.add('is-selected');
    const key = step.dataset.odKey || step.dataset.odStep;
    answers[key] = { value: button.dataset.value, label: button.dataset.label };
    log('antwoord', {
      vraag_key: key,
      vraag: step.dataset.odQuestionText || '',
      antwoord: button.dataset.label || '',
      waarde: button.dataset.value || '',
    });
    window.setTimeout(nextStep, 220);
  };

  const buildTags = () => {
    const tags = ['newsletter'];
    Object.entries(answers).forEach(([key, answer]) => {
      if (answer.value) tags.push(`quiz-${key}-${answer.value}`);
    });
    return tags.join(',');
  };

  const subscribe = async () => {
    if (!form) return;
    try {
      const body = new FormData(form);
      await window.fetch(form.action, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      });
    } catch (error) {
      /* abonneren is best-effort: de code tonen we sowieso */
    }
  };

  const revealCode = (email) => {
    writeFlag(window.localStorage, CLAIMED);
    applyReco();
    if (sentLine && email) {
      sentLine.textContent = sentLine.textContent.replace('%email%', email);
      sentLine.hidden = false;
    }
    showStep('code');
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (!form) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    if (tagsInput) tagsInput.value = buildTags();
    const email = (form.querySelector('input[type="email"]') || {}).value || '';
    if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');
    log('email', {
      email,
      tags: buildTags(),
      ...Object.fromEntries(Object.entries(answers).map(([key, answer]) => [`antwoord_${key}`, answer.label])),
    });
    const done = () => {
      if (submitBtn) submitBtn.removeAttribute('aria-busy');
      revealCode(email);
    };
    // Toon de code na het abonneren, maar wacht nooit langer dan 1,2 s.
    Promise.race([subscribe(), new Promise((resolve) => window.setTimeout(resolve, 1200))])
      .then(done)
      .catch(done);
  };

  const copyCode = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(code);
      ok = true;
    } catch (error) {
      ok = legacyCopy(code);
    }
    if (!ok || !copyBtn) return;
    copyBtn.classList.add('is-copied');
    const label = copyBtn.querySelector('[data-od-copy-label]');
    if (label) label.textContent = label.dataset.copied || 'Gekopieerd';
    log('kopieer');
    window.setTimeout(() => {
      copyBtn.classList.remove('is-copied');
      if (label) label.textContent = label.dataset.copy || label.textContent;
    }, 2200);
  };

  /* --- events ----------------------------------------------------------------- */
  root.querySelectorAll('[data-od-question]').forEach((step) => step.addEventListener('click', onOptionClick));
  root.querySelectorAll('[data-od-back]').forEach((button) => button.addEventListener('click', prevStep));
  root.querySelectorAll('[data-od-close]').forEach((button) =>
    button.addEventListener('click', () => closeScreen(button === closeBtn ? 'kruisje' : 'verder')));
  if (form) {
    form.addEventListener('submit', onSubmit);
    // Insurance: neutraliseer een programmatische native submit (bv. door een
    // extern script), zodat de pagina nooit herlaadt.
    try {
      form.submit = () => {};
    } catch (error) {
      /* submit niet overschrijfbaar: plain form navigeert toch niet zelf */
    }
  }
  if (copyBtn) copyBtn.addEventListener('click', copyCode);
  if (shopBtn) shopBtn.addEventListener('click', () => log('shop', { url: shopBtn.href }));
  if (tabOpenBtn) tabOpenBtn.addEventListener('click', () => openScreen('vouwtje'));
  if (tabCloseBtn) {
    tabCloseBtn.addEventListener('click', () => {
      writeFlag(window.sessionStorage, TAB_HIDDEN);
      hideTab();
    });
  }
  document.addEventListener('keydown', (event) => {
    if (!screen || screen.hidden) return;
    if (event.key === 'Escape' && closeReady) closeScreen('escape');
    if (event.key === 'Tab') trapFocus(event, screen);
  });

  const claimed = readFlag(window.localStorage, CLAIMED);
  if (claimed) showStep('code');

  if (designMode) {
    // In de theme-editor niet auto-openen; wel openen zodra deze sectie
    // geselecteerd wordt, en tonen als vouwtje zodat 'ie bereikbaar blijft.
    closeReady = true;
    showTab();
    document.addEventListener('shopify:section:select', (event) => {
      if (event.target && event.target.contains(root)) openScreen('editor');
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target && event.target.contains(root)) closeScreen('editor');
    });
    document.addEventListener('shopify:block:select', (event) => {
      if (!event.target || !root.contains(event.target)) return;
      const step = event.target.closest('[data-od-step]');
      if (step) showStep(step.dataset.odStep);
      const reco = event.target.closest('[data-od-reco]');
      if (reco) {
        showStep('code');
        recos.forEach((item) => { item.hidden = item !== reco; });
      }
    });
    return;
  }

  showTab();

  if (claimed) return; // niet meer auto-openen, vouwtje blijft
  const shownAt = readShownAt();
  if (shownAt && Date.now() - shownAt < repeatMs) return; // binnen de termijn al getoond
  if (mobileSecondPage && isMobile() && pageCount < 2) return; // Google interstitial-regel

  /* --- triggers --------------------------------------------------------------- */
  // Alle triggers delen een guard: de eerste die afgaat opent, ruimt via
  // disarmTriggers() de rest op, en de persistente status houdt hem dicht.
  let armed = true;
  const tryOpen = (trigger) => {
    if (!armed || opened) return;
    if (readFlag(window.localStorage, CLAIMED)) return;
    const last = readShownAt();
    if (last && Date.now() - last < repeatMs) return; // bv. net in een ander tabblad getoond
    openScreen(trigger);
  };

  const onScroll = () => {
    if (Date.now() - startedAt < MIN_DWELL_MS) return;
    const doc = document.documentElement;
    const max = doc.scrollHeight - window.innerHeight;
    if (max <= 0) return;
    if ((window.scrollY / max) * 100 >= scrollPercent) tryOpen('scroll');
  };

  const onLeave = (event) => {
    if (Date.now() - startedAt < MIN_DWELL_MS) return;
    if (event.clientY > 0) return;
    tryOpen('exit');
  };

  const preloadTimer = window.setTimeout(preloadMedia, Math.max(1500, delayMs - 4000));
  const openTimer = window.setTimeout(() => tryOpen('tijd'), delayMs);
  const useScroll = scrollPercent > 0;
  const useExit = exitIntent && !isMobile();
  if (useScroll) window.addEventListener('scroll', onScroll, { passive: true });
  if (useExit) document.addEventListener('mouseout', onLeave);

  disarmTriggers = () => {
    armed = false;
    window.clearTimeout(preloadTimer);
    window.clearTimeout(openTimer);
    if (useScroll) window.removeEventListener('scroll', onScroll);
    if (useExit) document.removeEventListener('mouseout', onLeave);
  };
};

/* --- helpers ------------------------------------------------------------------ */
function legacyCopy(text) {
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch (error) {
    return false;
  }
}

function trapFocus(event, container) {
  if (!container) return;
  const focusables = container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  const visible = Array.from(focusables).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
  if (visible.length === 0) return;
  const first = visible[0];
  const last = visible[visible.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDiscountPopup);
} else {
  initDiscountPopup();
}
